import { useState } from "react";

import { CopyButton } from "../../../copy-button";
import { DetectedFile } from "../../types";
import { ErrorDisplay } from "../ErrorDisplay";

interface LinkPreviewProps {
  data: string;
  detectedFile?: DetectedFile;
  imageError?: boolean;
}

export const LinkPreview = ({ data, detectedFile, imageError }: LinkPreviewProps) => {
  const [error, setError] = useState<string | null>(null);

  switch (detectedFile?.linkTypeInfo?.type) {
    case "image":
      if (imageError) {
        return (
          <div className="space-y-3">
            <div className="text-sm font-medium">Link Preview</div>

            {detectedFile?.error && (
              <ErrorDisplay
                title="Link Access Error"
                message="This link appears to be expired or restricted. You can still copy and use the URL, but it may not be accessible."
              />
            )}

            <div className="flex items-center justify-between gap-2 rounded border bg-gray-50 px-2 py-1.5">
              <div className="break-all text-sm text-gray-600">{data}</div>
              <CopyButton text={data} />
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-1">
          <div className="text-sm font-medium">Image Preview</div>
          <img
            src={data}
            className="max-h-80 max-w-full rounded object-contain"
            alt="link preview"
            onError={() => setError("Failed to load image")}
            onLoad={() => setError(null)}
          />
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
      );

    case "video":
      return (
        <div className="space-y-1">
          <div className="text-sm font-medium">Video Player</div>
          <video
            src={data}
            controls
            className="max-h-80 max-w-full rounded object-contain"
            onError={() => setError("Failed to load video")}
            onLoadStart={() => setError(null)}
          >
            Your browser does not support the video tag.
          </video>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
      );

    case "audio":
      return (
        <div className="space-y-1">
          <div className="text-sm font-medium">Audio Player</div>
          <audio
            src={data}
            controls
            className="w-full"
            onError={() => setError("Failed to load audio")}
            onLoadStart={() => setError(null)}
          >
            Your browser does not support the audio tag.
          </audio>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
      );

    case "pdf":
      return (
        <div className="space-y-1">
          <div className="text-sm font-medium">PDF Preview</div>
          <embed
            src={data}
            type="application/pdf"
            className="h-96 w-full max-w-[calc(95vw-2rem)] rounded"
            title="PDF Preview"
          />
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
      );

    default:
      return (
        <div className="space-y-3">
          <div className="text-sm font-medium">Link Preview</div>

          {detectedFile?.error && (
            <ErrorDisplay
              title="Link Access Error"
              message="This link appears to be expired or restricted. You can still copy and use the URL, but it may not be accessible."
            />
          )}

          <div className="flex items-center justify-between gap-2 rounded border bg-gray-50 px-2 py-1.5">
            <div className="break-all text-sm text-gray-600">{data}</div>
            <CopyButton text={data} />
          </div>
        </div>
      );
  }
};
